import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Клавишни комбинации. Диспечерът прави едни и същи няколко действия по
 * десетки пъти на ден — мишката е по-бавната от двете ръце.
 *
 * Формат на записа:
 *   'k'         — един клавиш
 *   'mod+k'     — Ctrl/Cmd
 *   'g b'       — акорд: първо g, после b (до 1.2 секунди пауза)
 */
export interface Hotkey {
  keys: string;
  description: string;
  run: (event: KeyboardEvent) => void;
  /** По подразбиране комбинациите не действат, докато се пише в поле. */
  allowInInput?: boolean;
  group?: string;
}

/**
 * Регистрацията чете списъка през референция, а не копие: обработчиците
 * се пресъздават при всяко рисуване и стар списък означава действие върху
 * стари данни — например „назначи първата заявка без бус", която вече е назначена.
 */
interface Registration {
  id: number;
  readonly hotkeys: Hotkey[];
}

const registrations: Registration[] = [];
const subscribers = new Set<() => void>();
let nextId = 1;
let installed = false;
let pendingPrefix: string | null = null;
let pendingTimer: number | null = null;

const CHORD_TIMEOUT_MS = 1200;

function notify(): void {
  for (const s of subscribers) s();
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.getAttribute('role') === 'textbox'
  );
}

function normalise(event: KeyboardEvent): string {
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  const mod = event.metaKey || event.ctrlKey;
  return mod ? `mod+${key}` : key;
}

function clearPrefix(): void {
  pendingPrefix = null;
  if (pendingTimer !== null) {
    window.clearTimeout(pendingTimer);
    pendingTimer = null;
  }
}

function allHotkeys(): Hotkey[] {
  // Последно регистрираните печелят — вложеният екран бие общата подредба.
  return registrations.flatMap((r) => r.hotkeys).reverse();
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.defaultPrevented || event.repeat) return;
  const pressed = normalise(event);
  const typing = isTypingTarget(event.target);

  if (pressed === 'Escape') clearPrefix();

  const candidates = allHotkeys();

  // Акорд: ако чакаме втори клавиш, търсим само двусъставни записи.
  if (pendingPrefix) {
    const combo = `${pendingPrefix} ${pressed}`;
    clearPrefix();
    const match = candidates.find((h) => h.keys === combo);
    if (match && (!typing || match.allowInInput)) {
      event.preventDefault();
      match.run(event);
    }
    return;
  }

  const direct = candidates.find((h) => h.keys === pressed);
  if (direct && (!typing || direct.allowInInput)) {
    event.preventDefault();
    direct.run(event);
    return;
  }

  const startsChord = candidates.some((h) => h.keys.startsWith(`${pressed} `));
  if (startsChord && !typing) {
    event.preventDefault();
    pendingPrefix = pressed;
    pendingTimer = window.setTimeout(clearPrefix, CHORD_TIMEOUT_MS);
  }
}

function install(): void {
  if (installed) return;
  window.addEventListener('keydown', handleKeyDown);
  installed = true;
}

/** Регистрира комбинации, докато компонентът е на екрана. */
export function useHotkeys(hotkeys: Hotkey[]): void {
  const latest = useRef(hotkeys);
  latest.current = hotkeys;

  useEffect(() => {
    install();
    const registration: Registration = {
      id: nextId++,
      get hotkeys() {
        return latest.current;
      },
    };
    registrations.push(registration);
    notify();
    return () => {
      const idx = registrations.findIndex((r) => r.id === registration.id);
      if (idx >= 0) registrations.splice(idx, 1);
      notify();
    };
  }, []);
}

function subscribe(fn: () => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

let snapshot: Hotkey[] = [];
function getSnapshot(): Hotkey[] {
  const current = registrations.flatMap((r) => r.hotkeys);
  if (current.length !== snapshot.length || current.some((h, i) => snapshot[i]?.keys !== h.keys)) {
    snapshot = current;
  }
  return snapshot;
}

/** Списъкът за прозореца „клавишни комбинации". */
export function useHotkeyList(): Hotkey[] {
  return useSyncExternalStore(subscribe, getSnapshot, () => snapshot);
}

/** Клавишите както се изписват на екрана. */
export function keyLabel(keys: string): string {
  return keys
    .split(' ')
    .map((part) =>
      part
        .replace('mod+', 'Ctrl+')
        .replace('Escape', 'Esc')
        .replace('ArrowDown', '↓')
        .replace('ArrowUp', '↑')
        .toUpperCase(),
    )
    .join(' след ');
}
